import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link RenameDeviceDialog}
 */
export type RenameDeviceDialogProps = {
    /** Whether the dialog is currently open */
    isOpen: boolean;
    /** Callback that should be called when closing the popup */
    onClose: () => void;
    /** Callback that should be called when the device was renamed */
    onRename: () => void;
    /** The device to rename */
    device: FullDevice | undefined;
};

/**
 * A dialog to rename a device
 */
export default function RenameDeviceDialog(props: RenameDeviceDialogProps) {
    const [t] = useTranslation("dialog-rename-device");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.device) return;
                const res = await ApiInternal.devices.setDnsname(props.device.uuid, { dnsname: value.name });

                if (res?.result === "Err") {
                    return {
                        fields: {
                            name: res.error.dnsname_not_unique
                                ? t("error.name-already-occupied")
                                : res.error.extra_dns_exists
                                  ? t("error.extra-dns-exists")
                                  : undefined,
                        },
                    };
                }
                props.onRename();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.device]);

    if (!props.device) return null;

    return (
        <Dialog open={true} onClose={() => props.onClose()}>
            <DialogTitle>{t("heading.rename-device", { old: props.device.dnsname })}</DialogTitle>
            <DialogDescription>{t("description.rename-device")}</DialogDescription>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-name")}</RequiredLabel>
                                        <Description>{t("description.new-name")}</Description>
                                        <Input
                                            type={"text"}
                                            pattern={"^[a-z]([a-z0-9\\-]{0,61}[a-z0-9])?$"}
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.rename-device")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
